/**
 * Axis-Aligned Bounding Box collision detection.
 * Works exclusively with hitbox data — never reads sprite/visual size.
 */
export class CollisionSystem {
    /**
     * Check if two entities' hitboxes overlap.
     * @param {import('../entities/BaseEntity.js').BaseEntity} a
     * @param {import('../entities/BaseEntity.js').BaseEntity} b
     * @returns {boolean}
     */
    static check(a, b) {
        const ra = a.getHitRect();
        const rb = b.getHitRect();
        return CollisionSystem.checkRects(ra, rb);
    }

    /**
     * Raw AABB test between two rect objects.
     * @param {{ x: number, y: number, w: number, h: number }} ra
     * @param {{ x: number, y: number, w: number, h: number }} rb
     * @returns {boolean}
     */
    static checkRects(ra, rb) {
        return (
            ra.x < rb.x + rb.w &&
            ra.x + ra.w > rb.x &&
            ra.y < rb.y + rb.h &&
            ra.y + ra.h > rb.y
        );
    }

    /**
     * Check one entity against an array of entities.
     * Returns the first entity that collides, or null.
     * Skips inactive entities automatically.
     * @param {import('../entities/BaseEntity.js').BaseEntity} entity
     * @param {import('../entities/BaseEntity.js').BaseEntity[]} others
     * @returns {import('../entities/BaseEntity.js').BaseEntity|null}
     */
    static checkAgainstList(entity, others) {
        const ra = entity.getHitRect();
        for (let i = 0; i < others.length; i++) {
            const other = others[i];
            if (!other.active || other === entity) continue;
            if (CollisionSystem.checkRects(ra, other.getHitRect())) {
                return other;
            }
        }
        return null;
    }

    /**
     * Check one entity against an array and return ALL colliding entities.
     * @param {import('../entities/BaseEntity.js').BaseEntity} entity
     * @param {import('../entities/BaseEntity.js').BaseEntity[]} others
     * @returns {import('../entities/BaseEntity.js').BaseEntity[]}
     */
    static checkAllAgainstList(entity, others) {
        const results = [];
        const ra = entity.getHitRect();
        for (let i = 0; i < others.length; i++) {
            const other = others[i];
            if (!other.active || other === entity) continue;
            if (CollisionSystem.checkRects(ra, other.getHitRect())) {
                results.push(other);
            }
        }
        return results;
    }
}
